import { useCallback, useEffect, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { getCartIdAtom } from 'src/store';
import { useContinueWithItems, useGetCartQuery } from 'src/hooks';
import { isBannedRetailer, isDisabledRetailer } from 'src/utils';

const TOTAL_TIMEOUT_MS = 20000;

/**
 * We mark `modalShown` as true when a modal is shown, regardless of the reason.
 * This is to prevent the same modal from being shown multiple times.
 * If a modal is shown, we don't want to show any other modals.
 *
 * `cartSent` can be true along with `hasError` because `hasMissing` is a subset of `hasError`
 *
 * When `tooLong` or `receiveUnsupported` are set to true, we display their respective modals.
 * These modals are rendered from the `Receive.tsx` file.
 *
 * If items are missing, we set `cartSent` to true to prevent the polling and tooLong timeout from continuing.
 * @returns
 */
export const useReceiveLogic = () => {
  const cartId = useAtomValue(getCartIdAtom);
  const [hasError, setHasError] = useState(false);
  const [hasMissing, setHasMissing] = useState(false);
  const [modalShown, setModalShown] = useState(false); // show no more than one modal on the page
  const [cartSent, setCartSent] = useState(false);
  const [tooLong, setTooLong] = useState(false);
  const [receiveUnsupported, setReceiveUnsupported] = useState(false);
  const [proceedToCartUrl, setProceedToCartUrl] = useState(''); // happens when the cart is missing items
  const [lastHeartbeat, setLastHeartbeat] = useState(0);

  const readyRef = useRef(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const cartQuery = useGetCartQuery({ cartId });
  const sendItems = useContinueWithItems();

  // update the modalShown state when the tooLong or receiveUnsupported state changes
  useEffect(() => {
    if (tooLong || receiveUnsupported) {
      setModalShown(true);
    }
  }, [tooLong, receiveUnsupported, setModalShown]);

  // Stop all receive logic if the retailer is blocked — the BlockedRetailerModal handles it
  useEffect(() => {
    if (cartQuery.data && (isDisabledRetailer(cartQuery.data) || isBannedRetailer(cartQuery.data))) {
      setModalShown(true);
    }
  }, [cartQuery.data]);

  // update the hasError state when the hasMissing, tooLong, or receiveUnsupported state changes
  useEffect(() => {
    setHasError(hasMissing || tooLong || receiveUnsupported);
  }, [hasMissing, tooLong, receiveUnsupported]);

  useEffect(() => {
    if (hasMissing && searchParams.has('d')) {
      setProceedToCartUrl(searchParams.get('d') || '');
    }
  }, [hasMissing, searchParams, setProceedToCartUrl]);

  // Keyed by cart id, not by the cart, so reloading the same cart neither restarts the taking-too-long timer nor re-adds the extension listener.
  const cartDataRef = useRef(cartQuery.data);
  useEffect(() => {
    cartDataRef.current = cartQuery.data;
  }, [cartQuery.data]);
  const loadedCartId = cartQuery.data?.id;

  const pushAnalytics = useCallback(
    (eventName: string, eventLabel?: string) => {
      const cart = cartDataRef.current;
      TagManager.dataLayer({
        dataLayer: {
          event: eventName,
          eventCategory: 'engagement',
          eventLabel: eventLabel ? eventLabel : 'Receive Page',
          eventValue: {
            cartValue: cart?.formattedCartTotalPrice || 0,
            cartCCY: cart?.cartCCY,
            cartId: loadedCartId,
            cartVendor: cart?.vendorDisplayName || cart?.vendor || '',
          },
        },
      });
    },
    [loadedCartId],
  );

  /**
   * Try to send the items once the cart timeout has been reached. This will try to perform the pushCartFinal func like the Get page does.
   * If the vendor isn't supported, it will return false.
   *
   * FLOW:
   * Once the cart timeout has been reached, we will display the RedirectionTakingTooLongModal.
   * Then, the modal button "Load Cart Manually" will need to be clicked, at which point we try to call
   * this function. If this function returns true, we know that the cart has been sent successfully and the page will be closed.
   * If the function returns false, we know the cart was not sent successfully and we will continue through the modal flow.
   */
  const trySendingItems = useCallback(async () => {
    if (!cartQuery.data) {
      return false;
    }

    // Returns false if the cart was not sent.
    // Returns 'receive_chunk' if the cart needs to be sent in chunks, i.e. Walmart
    // Returns void if the cart was sent successfully
    const status = await sendItems({
      id: cartId,
      cart: cartQuery.data,
      page: 'receive',
    });

    // explicitly check if false was returned as it could be undefined and
    // undefined means the cart was sent successfully, so we want to update below
    if (status === false) {
      return false;
    } else if (status === 'receive_chunk') {
      return 'receive_chunk';
    } else {
      setCartSent(true);
      return true;
    }
  }, [cartQuery.data, sendItems, cartId]);

  // If the cart's been sent or one of the modals has been shown,
  // we don't need to check for this anymore.
  useEffect(() => {
    // return early if the cart has been sent already
    if (cartSent || modalShown) {
      return;
    }

    const timer = setTimeout(() => {
      pushAnalytics('Receive Taking Too Long');
      setTooLong(true);
    }, TOTAL_TIMEOUT_MS);

    return () => clearTimeout(timer);
  }, [pushAnalytics, cartSent, modalShown, setTooLong, hasMissing, lastHeartbeat]);

  // Listen for 'steps' from the extension to know when the cart has been sent.
  // Technically, the extension removes the tab when this is completed, unless
  // there are missing items in the cart
  useEffect(() => {
    const onMsg = (event: MessageEvent) => {
      const d = event?.data;
      if (!d || d.direction !== 'from-extension' || !d.message) return;

      if (d.message.action === 'make-step') {
        pushAnalytics('Send to Vendor');
        readyRef.current = true;
        setCartSent(true);
      } else if (d.message.action === 'heartbeat') {
        setLastHeartbeat(Date.now());
      }
    };

    window.addEventListener('message', onMsg);

    return () => window.removeEventListener('message', onMsg);
  }, [pushAnalytics]);

  // Show the Unsupported Retailer Modal if the error is unsupported_retailer
  // and the cart has not been sent and a modal has not been shown already
  useEffect(() => {
    if (
      searchParams.has('error') &&
      searchParams.get('error') === 'unsupported_retailer' &&
      !cartSent &&
      !modalShown
    ) {
      setReceiveUnsupported(true);
    } else if (searchParams.has('missing')) {
      setHasMissing(true);
      setCartSent(true);
    }
  }, [modalShown, searchParams, cartSent]);

  // triggering an entire page reload from the extension which puts
  // unnecessary strain on the server as it triggers multiple server requests.
  useEffect(() => {
    // Stop polling if cart has been sent, or modal has been shown already
    // a modal will only be shown once, so we don't need to check for tooLong or hasError
    if (cartSent || modalShown) {
      return;
    }

    let prevSearch = window.location.search;
    const interval = setInterval(() => {
      if (window.location.search !== prevSearch) {
        prevSearch = window.location.search;
        // This will trigger a re-render and update searchParams from useSearchParams
        // by pushing a new entry to the history stack.
        // We use navigate to the same path with the new search string.
        navigate(
          {
            pathname: window.location.pathname,
            search: window.location.search,
          },
          { replace: true },
        );
      }
    }, 500); // Poll every 500ms

    return () => clearInterval(interval);
  }, [navigate, cartSent, modalShown]);

  return {
    hasError,
    hasMissing,
    tooLong,
    readyRef,
    trySendingItems,
    setTooLong,
    receiveUnsupported,
    setReceiveUnsupported,
    proceedToCartUrl,
  };
};
